import { classNames } from '@vkontakte/vkui';
import { isThisYear } from 'date-fns';
import parseISO from 'date-fns/parseISO';
import React, { type FC, memo, useCallback, useEffect } from 'react';
import { IS_WEBVIEW, IS_WEBVIEW_FROM_UA } from 'reactApp/appHelpers/configHelpers';
import { promotionEndDate } from 'reactApp/appHelpers/featuresHelpers';
import { marchPromo2026AnalyticsName, marchPromo2026Config } from 'reactApp/appHelpers/featuresHelpers/features/marchPromo2026';
import { cdnPackagesUrl } from 'reactApp/appHelpers/themeToolHelpers/default-values';
import { Container } from 'reactApp/sections/QuotaLanding/Container/Container';
import { revealPlatformPopup } from 'reactApp/sections/QuotaLanding/TariffsPlans/TariffPlansCard/TariffPlansCard.helpers';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { formatLocale } from 'reactApp/utils/formatDate';
import { sendDwh, sendXray } from 'reactApp/utils/ga';
import { getPlatformInfo } from 'reactApp/utils/gaHelpers';
import { makeMarchPromoURL } from 'reactApp/utils/marchPromo2026';
import opener from 'reactApp/utils/opener';

import styles from './PromoMarch2026.css';

interface PromoMarch2026Props {
    isMobile: boolean;
    isIOSPromo?: boolean;
    isAndroidPromo?: boolean;
    isMobilePayAvailable?: boolean;
}

const handleTermsClick = () => {
    opener(marchPromo2026Config.termsLink, true);
};

const source = 'main';
const platform = getPlatformInfo().join('_');

const handleButtonShow = () => {
    sendXray([`pr0326-btn-1tb3-shw`, platform], null, { sendImmediately: true });
    sendDwh({
        eventCategory: 'button',
        action: 'show',
        dwhData: {
            source,
            name: marchPromo2026AnalyticsName,
            platform,
        },
    });
};

const isWebview = IS_WEBVIEW || IS_WEBVIEW_FROM_UA;

export const PromoMarch2026: FC<PromoMarch2026Props> = memo(function PromoMarch2026(props) {
    const { isMobile, isIOSPromo = false, isAndroidPromo = false, isMobilePayAvailable } = props;

    const endDate = promotionEndDate || '2026-04-27';

    const endPromotionDate = formatLocale(parseISO(endDate), isThisYear(parseISO(endDate)) ? 'dd MMMM' : 'dd MMMM yyyy');

    useEffect(() => {
        handleButtonShow();
    }, []);

    const handleButtonClick = useCallback(() => {
        if ((isAndroidPromo || isIOSPromo) && !isMobilePayAvailable) {
            revealPlatformPopup(marchPromo2026Config.productId, isIOSPromo, isAndroidPromo);
            return;
        }

        sendXray([`pr0326-btn-1tb3-clk`, platform], null, { sendImmediately: true });
        sendDwh({
            eventCategory: 'button',
            action: 'click',
            dwhData: {
                source,
                name: marchPromo2026AnalyticsName,
                platform,
            },
        });

        const link = makeMarchPromoURL(marchPromo2026Config.tabMenuPromoBtnLink);
        opener(link, true);
    }, [isIOSPromo, isAndroidPromo, isMobilePayAvailable]);

    return (
        <Container className={classNames({ [styles.container_mobile]: isMobile && !isWebview })}>
            <div className={classNames(styles.root, { [styles.root_mobile]: isMobile })}>
                <span className={classNames(styles.terms, { [styles.terms_webview]: isWebview })} onClick={handleTermsClick}>
                    Акция Ого-го-Облако до&nbsp;{endPromotionDate}
                </span>
                <div className={classNames(styles.button_root, { [styles.button_root_mobile]: isMobile })} onClick={handleButtonClick}>
                    <Button primary theme="vk" sizeMode={isMobile ? ButtonSizeMode.small : ButtonSizeMode.middle} className={styles.button}>
                        Забрать 1 ТБ на 3 месяца
                    </Button>
                    <img
                        src={`${cdnPackagesUrl}/static/cloud/promo/march2026/${isMobile ? 'mobile' : 'desktop'}/sale_label.webp`}
                        className={classNames(styles.button_label, { [styles.button_label_mobile]: isMobile })}
                        alt="бесплатно"
                    />
                </div>
            </div>
        </Container>
    );
});

PromoMarch2026.displayName = 'PromoMarch2026';
